'use client';

import { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useRouter } from 'next/navigation';
import { createClient } from '@/lib/supabase/client';
import { X, Clock, CheckCircle, XCircle, ChevronLeft, ChevronRight, Award } from 'lucide-react';
import { Modal } from '@/components/ui/Modal';
import { Button } from '@/components/ui/button';
import { useLanguage } from '@/lib/i18n/LanguageContext';
import { useToast } from '@/lib/context/ToastContext';
import { useQueryClient } from '@tanstack/react-query';
import { queryKeys } from '@/lib/queries/queryKeys';
import CertificateGenerator from '@/components/certificates/CertificateGenerator';
import confetti from 'canvas-confetti';
import { fetchTestForTaking } from '@/lib/tests/testQuestions';

interface Question {
  id: string;
  question_text: string;
  type: 'multiple_choice' | 'single_choice' | 'true_false';
  points: number | null;
  options?: QuestionOption[];
}

// Participants never receive is_correct: the server grades the attempt.
interface QuestionOption {
  id: string;
  option_text: string;
}

export interface TestTakingOverlayProps {
  test: {
    id: string;
    title: string;
    description: string | null;
    type: string;
    passing_score: number | null;
    time_limit_minutes: number | null;
    max_attempts: number | null;
    available_until: string | null;
  };
  contentTitle: string;
  userId: string;
  enrollmentId: string;
  eventId: string;
  attemptCount: number;
  onClose: () => void;
  onComplete: (result: { score: number; passed: boolean }) => void;
}

type CardPhase = 'welcome' | 'questions' | 'review' | 'results';

export default function TestTakingOverlay({
  test,
  contentTitle,
  userId,
  enrollmentId,
  eventId,
  attemptCount,
  onClose,
  onComplete,
}: TestTakingOverlayProps) {
  const { t, language } = useLanguage();
  const toast = useToast();
  const queryClient = useQueryClient();
  const router = useRouter();
  const supabase = createClient();

  const [questions, setQuestions] = useState<Question[]>([]);
  const [loading, setLoading] = useState(true);
  const [answers, setAnswers] = useState<Record<string, string | string[]>>({});
  const [currentAttemptCount, setCurrentAttemptCount] = useState(attemptCount);
  const [currentIndex, setCurrentIndex] = useState(0); // 0=welcome, 1..N=questions, N+1=review
  const [phase, setPhase] = useState<CardPhase>('welcome');
  const [timeRemaining, setTimeRemaining] = useState<number | null>(null);
  const [testBegun, setTestBegun] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const submittingRef = useRef(false);
  const [resultScore, setResultScore] = useState<number | null>(null);
  const [resultPassed, setResultPassed] = useState<boolean | null>(null);
  const [showExitModal, setShowExitModal] = useState(false);
  const [highlightUnanswered, setHighlightUnanswered] = useState<string | null>(null);
  const [selectedAnimation, setSelectedAnimation] = useState<string | null>(null);
  const [certificateCreated, setCertificateCreated] = useState(false);
  const [certificateContentId, setCertificateContentId] = useState<string | null>(null);
  const [certificateContentTitle, setCertificateContentTitle] = useState<string | null>(null);
  const [showCertificateModal, setShowCertificateModal] = useState(false);

  // Swipe state
  const [dragX, setDragX] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);
  const dragStartRef = useRef<{ x: number; y: number; time: number } | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const totalCards = questions.length + 2; // welcome + questions + review

  // Fetch questions and fresh attempt count on mount
  useEffect(() => {
    const fetchQuestions = async () => {
      // Check TanStack Query cache first
      const cached = queryClient.getQueryData<(Question & { type: string })[]>(queryKeys.events.testQuestions(test.id));
      if (cached) {
        const supported = cached.filter(q => ['single_choice', 'multiple_choice', 'true_false'].includes(q.type)) as Question[];
        setQuestions(supported);
        setLoading(false);
        return;
      }

      try {
        const data = await fetchTestForTaking(supabase, test.id);
        queryClient.setQueryData(queryKeys.events.testQuestions(test.id), data);
        const supported = (data as (Question & { type: string })[]).filter(q =>
          ['single_choice', 'multiple_choice', 'true_false'].includes(q.type)
        ) as Question[];
        setQuestions(supported);
      } catch {
        // Leave the question list empty; the welcome card still renders.
      }
      setLoading(false);
    };

    const fetchFreshAttemptCount = async () => {
      const { data } = await supabase
        .from('test_attempts')
        .select('id')
        .eq('test_id', test.id)
        .eq('user_id', userId)
        .eq('enrollment_id', enrollmentId);
      if (data) {
        setCurrentAttemptCount(data.length);
      }
    };

    fetchQuestions();
    fetchFreshAttemptCount();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [test.id]);

  // Timer
  useEffect(() => {
    if (!testBegun || !test.time_limit_minutes) return;

    const endTime = Date.now() + test.time_limit_minutes * 60 * 1000;
    const interval = setInterval(() => {
      const remaining = Math.max(0, endTime - Date.now());
      setTimeRemaining(Math.floor(remaining / 1000));

      if (remaining === 0) {
        clearInterval(interval);
        handleSubmit(true);
      }
    }, 1000);

    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [testBegun, test.time_limit_minutes]);

  // Escape key
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (phase === 'results') {
          handleClose();
        } else {
          setShowExitModal(true);
        }
      }
      if (phase === 'questions' && !isAnimating) {
        if (e.key === 'ArrowRight') goNext();
        if (e.key === 'ArrowLeft') goPrev();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase, currentIndex, isAnimating, questions.length]);

  // Prevent body scroll
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const answeredCount = questions.filter(q => {
    const a = answers[q.id];
    if (Array.isArray(a)) return a.length > 0;
    return !!a;
  }).length;

  const isQuestionAnswered = (qId: string) => {
    const a = answers[qId];
    if (Array.isArray(a)) return a.length > 0;
    return !!a;
  };

  // Navigation
  const goNext = useCallback(() => {
    if (isAnimating) return;
    if (currentIndex < totalCards - 1) {
      setIsAnimating(true);
      setCurrentIndex(prev => prev + 1);
      if (currentIndex + 1 >= questions.length + 1) {
        setPhase('review');
      }
      setTimeout(() => setIsAnimating(false), 300);
    }
  }, [currentIndex, totalCards, isAnimating, questions.length]);

  const goPrev = useCallback(() => {
    if (isAnimating) return;
    if (currentIndex > 1) { // Can't go back to welcome
      setIsAnimating(true);
      setCurrentIndex(prev => prev - 1);
      if (currentIndex - 1 >= 1 && currentIndex - 1 <= questions.length) {
        setPhase('questions');
      }
      setTimeout(() => setIsAnimating(false), 300);
    }
  }, [currentIndex, isAnimating, questions.length]);

  const goToQuestion = useCallback((questionIndex: number) => {
    if (isAnimating) return;
    const targetIndex = questionIndex + 1; // +1 for welcome card
    setIsAnimating(true);
    setCurrentIndex(targetIndex);
    setPhase('questions');
    setTimeout(() => setIsAnimating(false), 300);
  }, [isAnimating]);

  // Swipe handlers
  const canSwipe = phase === 'questions';

  const handleDragStart = (clientX: number, clientY: number) => {
    if (!canSwipe || isAnimating) return;
    dragStartRef.current = { x: clientX, y: clientY, time: Date.now() };
    setIsDragging(true);
  };

  const handleDragMove = (clientX: number) => {
    if (!isDragging || !dragStartRef.current) return;
    const dx = clientX - dragStartRef.current.x;
    // Limit drag at edges
    if (currentIndex <= 1 && dx > 0) {
      setDragX(dx * 0.3); // Resistance at start
    } else if (currentIndex >= totalCards - 1 && dx < 0) {
      setDragX(dx * 0.3); // Resistance at end
    } else {
      setDragX(dx);
    }
  };

  const handleDragEnd = () => {
    if (!isDragging || !dragStartRef.current) return;
    setIsDragging(false);

    const threshold = 80;
    if (dragX < -threshold && currentIndex < totalCards - 1) {
      goNext();
    } else if (dragX > threshold && currentIndex > 1) {
      goPrev();
    }

    setDragX(0);
    dragStartRef.current = null;
  };

  const onTouchStart = (e: React.TouchEvent) => {
    handleDragStart(e.touches[0].clientX, e.touches[0].clientY);
  };

  const onTouchMove = (e: React.TouchEvent) => {
    handleDragMove(e.touches[0].clientX);
  };

  const onTouchEnd = () => handleDragEnd();

  const onMouseDown = (e: React.MouseEvent) => {
    handleDragStart(e.clientX, e.clientY);
  };

  const onMouseMove = (e: React.MouseEvent) => {
    if (isDragging) {
      e.preventDefault();
      handleDragMove(e.clientX);
    }
  };

  const onMouseUp = () => handleDragEnd();
  const onMouseLeave = () => {
    if (isDragging) handleDragEnd();
  };

  // Answer handlers
  const handleSingleAnswer = (questionId: string, optionId: string) => {
    setAnswers(prev => ({ ...prev, [questionId]: optionId }));
    setSelectedAnimation(optionId);

    // Auto-advance after animation
    setTimeout(() => {
      setSelectedAnimation(null);
      if (currentIndex < totalCards - 1) {
        goNext();
      }
    }, 400);
  };

  const handleMultipleAnswer = (questionId: string, optionId: string, checked: boolean) => {
    setAnswers(prev => {
      const current = Array.isArray(prev[questionId]) ? (prev[questionId] as string[]) : [];
      const updated = checked
        ? [...current, optionId]
        : current.filter(id => id !== optionId);
      return { ...prev, [questionId]: updated };
    });
  };

  // Begin test
  const handleBegin = () => {
    setTestBegun(true);
    setPhase('questions');
    setIsAnimating(true);
    setCurrentIndex(1);
    setTimeout(() => setIsAnimating(false), 300);
  };

  // Submit
  const handleSubmit = async (isAutoSubmit = false) => {
    if (submittingRef.current) return;
    submittingRef.current = true;

    // Validation (skip if auto-submit from timer)
    if (!isAutoSubmit && answeredCount < questions.length) {
      const firstUnanswered = questions.findIndex(q => !isQuestionAnswered(q.id));
      if (firstUnanswered >= 0) {
        setHighlightUnanswered(questions[firstUnanswered].id);
        goToQuestion(firstUnanswered);
        toast.warning(t('event.test.answerAllQuestions'));
        submittingRef.current = false;
        return;
      }
    }

    setSubmitting(true);

    // Save attempt via server-side API: it grades the answers (the correct
    // options never reach the browser) and enforces max_attempts.
    let response: Response;
    try {
      response = await fetch('/api/test-attempts', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          test_id: test.id,
          enrollment_id: enrollmentId,
          answers,
        }),
      });
    } catch {
      toast.error(t('testTaking.errorSubmitting'));
      setSubmitting(false);
      submittingRef.current = false;
      return;
    }

    if (response.status === 409) {
      // Max attempts exceeded (another tab used them). Nothing was recorded
      // or graded, so there is no result to show.
      const data = await response.json().catch(() => null);
      setCurrentAttemptCount(data?.attemptCount ?? currentAttemptCount);
      toast.error(t('event.test.maxAttemptsReached'));
      setSubmitting(false);
      submittingRef.current = false;
      queryClient.invalidateQueries({ queryKey: queryKeys.events.userTestAttempts(eventId, enrollmentId) });
      onClose();
      return;
    }

    if (response.status === 403) {
      // A completed refund revokes access to the test (D1/R4) - show the
      // specific reason instead of the generic submission error.
      const data = await response.json().catch(() => null);
      if (data?.errorCode === 'ENROLLMENT_REFUNDED' || data?.errorCode === 'TEST_NOT_AVAILABLE_FOR_TICKET') {
        toast.error(t(`profile.tests.errors.${data.errorCode}`));
        setSubmitting(false);
        submittingRef.current = false;
        return;
      }
    }

    if (!response.ok) {
      toast.error(t('testTaking.errorSubmitting'));
      setSubmitting(false);
      submittingRef.current = false;
      return;
    }

    const responseData = await response.json();
    setCurrentAttemptCount(responseData.attemptCount ?? currentAttemptCount + 1);
    const finalScore: number = typeof responseData.score === 'number' ? responseData.score : 0;
    const testPassed = responseData.passed === true;

    // Certificate auto-generation is handled atomically by a DB trigger on
    // enrollment_milestones (test_passed). Verify cert exists, retry if trigger failed.
    if (responseData.pendingCertCreated) {
      try {
        const { data: cert } = await supabase
          .from('certifications')
          .select('id, content:content_id(title)')
          .eq('user_id', userId)
          .eq('content_id', responseData.contentId)
          .maybeSingle();

        if (cert) {
          setCertificateCreated(true);
          setCertificateContentId(responseData.contentId);
          setCertificateContentTitle((cert.content as { title?: string })?.title || '');
        } else {
          // Trigger failed – retry via auto-generate endpoint
          const retryRes = await fetch('/api/certificates/auto-generate', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ contentId: responseData.contentId }),
          });
          if (retryRes.ok) {
            const retryData = await retryRes.json();
            if (retryData.pendingCertCreated || retryData.alreadyExists) {
              setCertificateCreated(true);
              setCertificateContentId(responseData.contentId);
              setCertificateContentTitle(retryData.contentTitle || '');
            }
          }
        }
      } catch {
        // Non-critical – don't block test results
      }
    }

    // Invalidate user progress queries
    queryClient.invalidateQueries({ queryKey: queryKeys.events.userTestAttempts(eventId, enrollmentId) });
    queryClient.invalidateQueries({ queryKey: queryKeys.events.testHistory(eventId, enrollmentId) });

    setResultScore(finalScore);
    setResultPassed(testPassed);
    setPhase('results');
    setSubmitting(false);
    submittingRef.current = false;

    // Fire confetti on pass
    if (testPassed) {
      setTimeout(() => {
        confetti({
          particleCount: 150,
          spread: 70,
          origin: { y: 0.6 },
        });
      }, 300);
    }
  };

  const handleClose = () => {
    if (resultScore !== null && resultPassed !== null) {
      onComplete({ score: resultScore, passed: resultPassed });
    } else {
      onClose();
    }
  };

  const handleGetCertificate = () => {
    if (resultScore !== null && resultPassed !== null) {
      onComplete({ score: resultScore, passed: resultPassed });
    }
    router.push('/profile/certificates');
  };

  const handleTryAgain = () => {
    setAnswers({});
    setCurrentIndex(0);
    setPhase('welcome');
    setTestBegun(false);
    setTimeRemaining(null);
    setResultScore(null);
    setResultPassed(null);
    setHighlightUnanswered(null);
    submittingRef.current = false;
  };

  // Format deadline
  const formatDeadline = (dateStr: string) => {
    const d = new Date(dateStr);
    const locale = language === 'uk' ? 'uk-UA' : 'en-GB';
    return d.toLocaleDateString(locale, {
      day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit',
    });
  };

  // Render cards
  const renderWelcomeCard = () => {
    // Hide subtitle when it closely matches the test title (avoids redundant text on small screens)
    const showSubtitle = contentTitle && test.title.localeCompare(contentTitle, undefined, { sensitivity: 'base' }) !== 0
      && !test.title.includes(contentTitle) && !contentTitle.includes(test.title);

    return (
      <div className="flex flex-col items-center px-6 py-6 text-center">
        <div className="max-w-md w-full">
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900 mb-1">{test.title}</h1>
          {showSubtitle && <p className="text-sm text-gray-500">{contentTitle}</p>}

          <div className="space-y-0 text-left mt-4 mb-4">
            <div className="flex justify-between items-center py-2 border-b border-gray-100">
              <span className="text-gray-600">{t('event.test.questionsCount')}</span>
              <span className="font-semibold text-gray-900">{questions.length}</span>
            </div>

            {test.passing_score && (
              <div className="flex justify-between items-center py-2 border-b border-gray-100">
                <span className="text-gray-600">{t('courseDetails.passingScore')}</span>
                <span className="font-semibold text-gray-900">{test.passing_score}%</span>
              </div>
            )}

            {test.max_attempts && (
              <div className="flex justify-between items-center py-2 border-b border-gray-100">
                <span className="text-gray-600">{t('event.test.attemptsRemaining')}</span>
                <span className="font-semibold text-gray-900">
                  {test.max_attempts - currentAttemptCount} / {test.max_attempts}
                </span>
              </div>
            )}

            {test.available_until && (
              <div className="flex justify-between items-center py-2 border-b border-gray-100">
                <span className="text-gray-600">{t('event.test.deadline')}</span>
                <span className="font-semibold text-gray-900 text-right text-sm">
                  {formatDeadline(test.available_until)}
                </span>
              </div>
            )}

            {test.time_limit_minutes && (
              <div className="flex justify-between items-center py-2 border-b border-gray-100">
                <span className="text-gray-600">{t('courseDetails.timeLimit')}</span>
                <span className="font-semibold text-gray-900">
                  {test.time_limit_minutes} {t('common.minutes')}
                </span>
              </div>
            )}
          </div>

          <Button
            onClick={handleBegin}
            className="w-full"
            size="lg"
            disabled={loading || (!!test.max_attempts && currentAttemptCount >= test.max_attempts)}
          >
            {loading ? t('event.test.loading') : t('event.test.beginTest')}
          </Button>
          {test.max_attempts && currentAttemptCount >= test.max_attempts && (
            <p className="mt-2 text-sm text-red-600 font-medium">
              {t('event.test.noAttemptsRemaining')}
            </p>
          )}
          <button
            onClick={() => setShowExitModal(true)}
            className="mt-4 text-gray-500 hover:text-gray-700 text-sm transition-colors"
          >
            {t('event.test.cancel')}
          </button>
        </div>
      </div>
    );
  };

  const renderQuestionCard = (question: Question, questionIndex: number) => {
    const isHighlighted = highlightUnanswered === question.id;

    return (
      <div
        className={`flex flex-col min-h-full px-4 sm:px-6 py-6 ${
          isHighlighted ? 'ring-2 ring-red-300 ring-inset rounded-lg' : ''
        }`}
        onAnimationEnd={() => {
          if (isHighlighted) setHighlightUnanswered(null);
        }}
      >
        <div className="flex-1 max-w-2xl mx-auto w-full">
          <p className="text-sm text-gray-500 mb-4">
            {t('event.test.questionOf')} {questionIndex + 1} / {questions.length}
          </p>
          <h2 className="text-lg sm:text-xl font-semibold text-gray-900 mb-6">
            {question.question_text}
          </h2>

          <div className="space-y-3">
            {question.options?.map((option) => {
              const isSelected = question.type === 'multiple_choice'
                ? Array.isArray(answers[question.id]) && (answers[question.id] as string[]).includes(option.id)
                : answers[question.id] === option.id;
              const isAnimatingSelection = selectedAnimation === option.id;

              return (
                <button
                  key={option.id}
                  onClick={() => {
                    if (question.type === 'multiple_choice') {
                      handleMultipleAnswer(question.id, option.id, !isSelected);
                    } else {
                      handleSingleAnswer(question.id, option.id);
                    }
                  }}
                  className={`w-full text-left p-4 rounded-xl border-2 transition-all duration-200 ${
                    isSelected
                      ? isAnimatingSelection
                        ? 'border-blue-500 bg-blue-50 scale-[1.02]'
                        : 'border-blue-500 bg-blue-50'
                      : 'border-gray-200 hover:border-gray-300 bg-white'
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <div className={`w-5 h-5 mt-0.5 flex-shrink-0 rounded-${question.type === 'multiple_choice' ? 'sm' : 'full'} border-2 flex items-center justify-center transition-colors ${
                      isSelected ? 'border-blue-500 bg-blue-500' : 'border-gray-300'
                    }`}>
                      {isSelected && (
                        <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                        </svg>
                      )}
                    </div>
                    <span className="flex-1 text-gray-800 break-words">{option.option_text}</span>
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* Navigation arrows at bottom */}
        <div className="flex items-center justify-between mt-6 max-w-2xl mx-auto w-full">
          <button
            onClick={goPrev}
            disabled={currentIndex <= 1}
            className="flex items-center gap-1 text-gray-500 hover:text-gray-700 disabled:opacity-30 disabled:cursor-not-allowed transition-colors p-2"
          >
            <ChevronLeft className="h-5 w-5" />
            <span className="text-sm">{t('event.test.previous')}</span>
          </button>

          {question.type === 'multiple_choice' && (
            <Button onClick={goNext} size="sm">
              {currentIndex >= totalCards - 2 ? t('event.test.reviewTitle') : t('event.test.next')}
            </Button>
          )}

          <button
            onClick={goNext}
            disabled={currentIndex >= totalCards - 1}
            className="flex items-center gap-1 text-gray-500 hover:text-gray-700 disabled:opacity-30 disabled:cursor-not-allowed transition-colors p-2"
          >
            <span className="text-sm">{t('event.test.next')}</span>
            <ChevronRight className="h-5 w-5" />
          </button>
        </div>
      </div>
    );
  };

  const renderReviewCard = () => (
    <div className="flex flex-col min-h-full px-4 sm:px-6 py-6">
      <div className="flex-1 max-w-2xl mx-auto w-full">
        <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2">
          {t('event.test.reviewTitle')}
        </h2>
        <p className="text-gray-600 mb-6">
          {answeredCount} / {questions.length} {t('event.test.answeredCount')}
        </p>

        <div className="space-y-2 mb-8">
          {questions.map((q, i) => {
            const answered = isQuestionAnswered(q.id);
            return (
              <button
                key={q.id}
                onClick={() => goToQuestion(i)}
                className={`w-full flex items-center justify-between p-3 rounded-lg border text-left transition-colors ${
                  answered
                    ? 'border-gray-200 bg-white hover:bg-gray-50'
                    : 'border-red-200 bg-red-50 hover:bg-red-100'
                }`}
              >
                <div className="flex items-center gap-3 min-w-0">
                  <span className={`text-sm font-medium flex-shrink-0 ${answered ? 'text-gray-500' : 'text-red-600'}`}>
                    {i + 1}.
                  </span>
                  <span className="text-sm text-gray-800 truncate">
                    {q.question_text.length > 60 ? q.question_text.substring(0, 60) + '...' : q.question_text}
                  </span>
                </div>
                <div className="flex-shrink-0 ml-2">
                  {answered ? (
                    <CheckCircle className="h-5 w-5 text-green-500" />
                  ) : (
                    <span className="text-xs font-medium text-red-600 whitespace-nowrap">
                      {t('event.test.notAnswered')}
                    </span>
                  )}
                </div>
              </button>
            );
          })}
        </div>

        <div className="flex flex-col gap-3">
          <Button
            onClick={() => handleSubmit(false)}
            disabled={submitting}
            className="w-full"
            size="lg"
          >
            {submitting ? t('testTaking.submitting') : t('event.test.submitTest')}
          </Button>
          <button
            onClick={() => setShowExitModal(true)}
            className="text-gray-500 hover:text-gray-700 text-sm transition-colors text-center"
          >
            {t('event.test.cancel')}
          </button>
        </div>
      </div>
    </div>
  );

  const renderResultsCard = () => {
    const passed = resultPassed;
    const score = resultScore ?? 0;
    const maxAttemptsLeft = test.max_attempts
      ? test.max_attempts - currentAttemptCount
      : null;

    return (
      <div className="flex flex-col items-center justify-center min-h-full px-6 py-8 text-center">
        <div className="max-w-md w-full">
          {passed ? (
            <>
              <div className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="h-10 w-10 text-green-500" />
              </div>
              <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-1">
                {t('event.test.congratulations')}
              </h2>
              <p className="text-lg text-gray-700 mb-4">
                {t('event.test.youPassedTest')}
              </p>
              <p className="text-sm text-gray-500">{t('event.test.yourScore')}</p>
              <p className="text-5xl font-bold text-green-600 my-3">{score}%</p>
              <p className="text-gray-600 mb-8">
                {t('event.test.passingPercentage')}: {test.passing_score ?? 70}%
              </p>
            </>
          ) : (
            <>
              <div className="w-20 h-20 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-6">
                <XCircle className="h-10 w-10 text-red-500" />
              </div>
              <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">
                {t('event.test.notPassedTitle')}
              </h2>
              <p className="text-5xl font-bold text-red-600 my-6">{score}%</p>
              <p className="text-gray-600 mb-4">
                {t('event.test.retryEncouragement').replace('{percentage}', String(test.passing_score ?? 70))}
              </p>
              {maxAttemptsLeft !== null && maxAttemptsLeft > 0 && (
                <p className="text-sm text-gray-500 mb-4">
                  {t('event.test.attemptsRemaining')}: {maxAttemptsLeft}
                </p>
              )}
            </>
          )}

          {certificateCreated && passed && (
            <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg">
              <div className="flex items-center justify-center gap-2">
                <Award className="h-5 w-5 text-green-600" />
                <span className="font-semibold text-green-800">
                  {t('certificate.certificateEligible')}
                </span>
              </div>
            </div>
          )}

          <div className="flex flex-col gap-3">
            {!passed && maxAttemptsLeft !== null && maxAttemptsLeft > 0 && (
              <Button onClick={handleTryAgain} className="w-full" size="lg">
                {t('event.test.tryAgain')}
              </Button>
            )}
            {passed && certificateCreated ? (
              <Button onClick={handleGetCertificate} className="w-full" size="lg">
                {t('certificate.getCertificate')}
              </Button>
            ) : (
              <Button
                onClick={handleClose}
                variant={passed ? 'default' : 'secondary'}
                className="w-full"
                size="lg"
              >
                {t('event.test.close')}
              </Button>
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderCurrentCard = () => {
    if (phase === 'results') return renderResultsCard();
    if (currentIndex === 0) return renderWelcomeCard();
    if (currentIndex > questions.length) return renderReviewCard();

    const questionIndex = currentIndex - 1;
    const question = questions[questionIndex];
    if (!question) return null;
    return renderQuestionCard(question, questionIndex);
  };

  // Progress bar
  const progressPercent = phase === 'questions' || phase === 'review'
    ? Math.round(((currentIndex) / totalCards) * 100)
    : 0;

  const content = (
    <div className="fixed inset-0 z-50 bg-white flex flex-col">
      {/* Header bar */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 bg-white flex-shrink-0">
        <button
          onClick={() => {
            if (phase === 'results') handleClose();
            else setShowExitModal(true);
          }}
          className="p-1.5 -ml-1.5 text-gray-500 hover:text-gray-700 transition-colors"
          aria-label="Close"
        >
          <X className="h-5 w-5" />
        </button>

        {/* Progress bar - only during questions */}
        {testBegun && phase !== 'results' && (
          <div className="flex-1 mx-4">
            <div className="h-1.5 bg-gray-200 rounded-full overflow-hidden">
              <div
                className="h-full bg-blue-500 rounded-full transition-all duration-300"
                style={{ width: `${progressPercent}%` }}
              />
            </div>
          </div>
        )}

        {/* Timer */}
        {testBegun && timeRemaining !== null && phase !== 'results' && (
          <div className={`flex items-center gap-1.5 text-sm font-medium ${
            timeRemaining < 60 ? 'text-red-600' : 'text-gray-700'
          }`}>
            <Clock className="h-4 w-4" />
            <span>{formatTime(timeRemaining)}</span>
          </div>
        )}

        {/* Spacer when no timer */}
        {(!testBegun || timeRemaining === null || phase === 'results') && (
          <div className="w-8" />
        )}
      </div>

      {/* Card content area */}
      <div
        ref={containerRef}
        className="flex-1 overflow-y-auto overflow-x-hidden"
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
        onMouseDown={onMouseDown}
        onMouseMove={onMouseMove}
        onMouseUp={onMouseUp}
        onMouseLeave={onMouseLeave}
      >
        <div
          className="min-h-full"
          style={{
            transform: isDragging ? `translateX(${dragX}px)` : 'translateX(0)',
            transition: isDragging ? 'none' : 'transform 300ms ease-out',
          }}
        >
          {renderCurrentCard()}
        </div>
      </div>

      {/* Certificate generator modal */}
      {showCertificateModal && certificateContentId && (
        <CertificateGenerator
          courseId={certificateContentId}
          courseName={certificateContentTitle || ''}
          onClose={() => setShowCertificateModal(false)}
        />
      )}

      {/* Exit confirmation modal */}
      <Modal
        isOpen={showExitModal}
        onClose={() => setShowExitModal(false)}
        title={t('event.confirmExit')}
        description={t('event.progressWillBeLost')}
        size="sm"
        showCloseButton={false}
        zIndex={60}
      >
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowExitModal(false)}>
            {t('common.cancel')}
          </Button>
          <Button variant="destructive" onClick={() => { setShowExitModal(false); onClose(); }}>
            {t('event.exitAnyway')}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );

  // Use portal to render at document body level
  if (typeof window === 'undefined') return null;
  return createPortal(content, document.body);
}
